import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { saveAs } from 'file-saver'
import { format } from 'utils/datetime'
import { useAuth } from 'hooks'
import { axiosApi } from 'services/api'
import { updateUserSync, syncSharedFiles } from 'features/sync-data/syncSlice'
import { getPatientsWithSummary, reInitialize } from 'features/patients-list/patientSlice'
import { setCollection, getCollections } from 'features/collections-list/collectionSlice'
import { getFavoriteFiles } from 'features/favorite/favoriteSlice'
import { recacheIndicators } from 'features/patient-file-thumbnail/thumbnailSlice'
import { unAuthenticate } from 'features/auth/authSlice'

export function useSignalRAction() {
    const dispatch = useDispatch()
    const { fetchUserData } = useAuth()

    const action = useCallback(async (act, _meta) => {
        const meta = JSON.parse(_meta)

        if (act === 'updateSync') {
            const isSyncing = !['completed', 'aborted', 'failed'].includes(meta.status)
            const lastSync = meta.status === 'completed' ? meta.updatedDateTime : null
            dispatch(updateUserSync({ isSyncing, lastSync }))
            if (meta.status === 'completed') {
                await dispatch(getPatientsWithSummary())
                dispatch(axiosApi.util.invalidateTags(['PatientFiles']))
            }
        } else if (act === 'startDownload') {
            if (meta.status === 'completed' && meta.downloadUri !== '') {
                saveAs(
                    meta.downloadUri,
                    `Zeiss_Surgical_Cloud_Download_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}`
                )
            }
        } else if (act === 'deletePatientFiles' && meta.status === 'success') {
            dispatch(reInitialize())
            dispatch(setCollection(null))
            dispatch(getPatientsWithSummary())
            dispatch(getCollections())
            dispatch(
                axiosApi.util.invalidateTags([
                    'PatientFiles',
                    'CollectionFiles',
                    'Tag',
                    'AggregateTags',
                ])
            )
        } else if (act === 'activateConsent') {
            dispatch(syncSharedFiles())
        } else if (['consentActivated', 'unShare', 'refetchSharedFiles'].includes(act)) {
            dispatch(axiosApi.util.invalidateTags(['SharedSummary', 'SharedFiles']))
        } else if (act === 'recacheIndicators') {
            dispatch(recacheIndicators())
        } else if (act === 'updateFavorites') {
            dispatch(getFavoriteFiles())
        } else if (act === 'deleteUser') {
            if (!meta) {
                dispatch(axiosApi.util.invalidateTags([{ type: 'User', id: 'LIST' }]))
            } else {
                dispatch(
                    axiosApi.util.updateQueryData('getUsers', meta.agreementId, draft => {
                        const index = draft.findIndex(u => u.id === meta.userId)
                        if (index !== -1) draft.splice(index, 1)
                    })
                )
            }
        } else if (act === 'authUserSync') {
            fetchUserData()
        } else if (act === 'inviteUser') {
            dispatch(axiosApi.util.invalidateTags([{ type: 'User', id: 'LIST' }]))
        } else if (act === 'logout') {
            dispatch(unAuthenticate())
        }
    }, [])

    return useMemo(
        () => ({
            action,
        }),
        [action]
    )
}
